/**
 * PolicyManagement - Main Policy Management Component
 *
 * Container component for policy CRUD operations with list view,
 * search/filter, pagination, and modal management.
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Form, Table, Badge, Dropdown, InputGroup, Alert, Card, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/apiClient';
import type { Policy, Entity } from '@/types/policy';
import { getErrorMessage } from '@/utils/errorHandling';
import PolicyEditorModal from './PolicyEditorModal';
import PolicyAttachModal from './PolicyAttachModal';
import PolicyHistoryModal from './PolicyHistoryModal';
import PolicyCloneModal from './PolicyCloneModal';

export default function PolicyManagement() {
  const { t } = useTranslation();

  // State
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPolicies, setTotalPolicies] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTemplate, setFilterTemplate] = useState<boolean | undefined>(undefined);

  // Modals
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);

  // Entity data for policy builder
  const [studies, setStudies] = useState<Entity[]>([]);
  const [clinics, setClinics] = useState<Entity[]>([]);
  const [devices, setDevices] = useState<Entity[]>([]);
  const [dataTypes, setDataTypes] = useState<Entity[]>([]);
  const [users, setUsers] = useState<Entity[]>([]);
  const [userGroups, setUserGroups] = useState<Entity[]>([]);
  const [roles, setRoles] = useState<Entity[]>([]);

  const [isRefreshingStudies, setIsRefreshingStudies] = useState(false);
  const [isRefreshingClinics, setIsRefreshingClinics] = useState(false);
  const [isRefreshingDevices, setIsRefreshingDevices] = useState(false);
  const [isRefreshingDataTypes, setIsRefreshingDataTypes] = useState(false);
  const [isRefreshingUsers, setIsRefreshingUsers] = useState(false);
  const [isRefreshingUserGroups, setIsRefreshingUserGroups] = useState(false);
  const [isRefreshingRoles, setIsRefreshingRoles] = useState(false);

  const loadPolicies = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.listPolicies({
        is_template: filterTemplate,
        page: currentPage,
        per_page: perPage,
      });

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      const policyData = response.data as { items?: Policy[]; total?: number; pages?: number } | undefined;
      if (policyData) {
        setPolicies(policyData.items || []);
        setTotalPolicies(policyData.total || 0);
        setTotalPages(policyData.pages || 1);
      }

      setIsLoading(false);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to load policies');
      setIsLoading(false);
    }
  }, [currentPage, perPage, filterTemplate]);

  const loadEntities = useCallback(async () => {
    try {
      // Load all entity types in parallel
      const [studiesRes, clinicsRes, devicesRes, dataTypesRes, usersRes, groupsRes, rolesRes] =
        await Promise.all([
          apiClient.getAdminStudies(),
          apiClient.getAdminClinics(),
          apiClient.getAdminDevices(),
          apiClient.getAdminDataTypes(),
          apiClient.getAdminUsers({ skip: 0, limit: 1000 }),
          apiClient.getAdminGroups({ skip: 0, limit: 1000 }),
          apiClient.getAdminRoles(),
        ]);

      if (studiesRes.data) setStudies(studiesRes.data as Entity[]);
      if (clinicsRes.data) setClinics(clinicsRes.data as Entity[]);
      if (devicesRes.data) setDevices(Array.isArray(devicesRes.data) ? devicesRes.data : []);
      if (dataTypesRes.data) setDataTypes(Array.isArray(dataTypesRes.data) ? dataTypesRes.data : []);
      if (usersRes.data) setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      if (groupsRes.data) setUserGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      if (rolesRes.data) setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    } catch (err) {
      console.error('Failed to load entity data:', err);
    }
  }, []);

  // Load policies on mount and when filters change
  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  // Load entity data once
  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  // Individual refresh handlers
  const handleRefreshStudies = useCallback(async () => {
    setIsRefreshingStudies(true);
    try {
      const response = await apiClient.getAdminStudies();
      if (response.data) setStudies(response.data as Entity[]);
    } catch (err) {
      console.error('Failed to refresh studies:', err);
    } finally {
      setIsRefreshingStudies(false);
    }
  }, []);

  const handleRefreshClinics = useCallback(async () => {
    setIsRefreshingClinics(true);
    try {
      const response = await apiClient.getAdminClinics();
      if (response.data) setClinics(response.data as Entity[]);
    } catch (err) {
      console.error('Failed to refresh clinics:', err);
    } finally {
      setIsRefreshingClinics(false);
    }
  }, []);

  const handleRefreshDevices = useCallback(async () => {
    setIsRefreshingDevices(true);
    try {
      const response = await apiClient.getAdminDevices();
      if (response.data) setDevices(response.data as Entity[]);
    } catch (err) {
      console.error('Failed to refresh devices:', err);
    } finally {
      setIsRefreshingDevices(false);
    }
  }, []);

  const handleRefreshDataTypes = useCallback(async () => {
    setIsRefreshingDataTypes(true);
    try {
      const response = await apiClient.getAdminDataTypes();
      if (response.data) setDataTypes(response.data as Entity[]);
    } catch (err) {
      console.error('Failed to refresh data types:', err);
    } finally {
      setIsRefreshingDataTypes(false);
    }
  }, []);

  const handleRefreshUsers = useCallback(async () => {
    setIsRefreshingUsers(true);
    try {
      const response = await apiClient.getAdminUsers({ skip: 0, limit: 1000 });
      if (response.data) setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to refresh users:', err);
    } finally {
      setIsRefreshingUsers(false);
    }
  }, []);

  const handleRefreshUserGroups = useCallback(async () => {
    setIsRefreshingUserGroups(true);
    try {
      const response = await apiClient.getAdminGroups({ skip: 0, limit: 1000 });
      if (response.data) setUserGroups(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to refresh user groups:', err);
    } finally {
      setIsRefreshingUserGroups(false);
    }
  }, []);

  const handleRefreshRoles = useCallback(async () => {
    setIsRefreshingRoles(true);
    try {
      const response = await apiClient.getAdminRoles();
      if (response.data) setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to refresh roles:', err);
    } finally {
      setIsRefreshingRoles(false);
    }
  }, []);

  const handleCreatePolicy = () => {
    setSelectedPolicy(null);
    setShowEditorModal(true);
  };

  const handleEditPolicy = (policy: Policy) => {
    setSelectedPolicy(policy);
    setShowEditorModal(true);
  };

  const handleAttachPolicy = (policy: Policy) => {
    setSelectedPolicy(policy);
    setShowAttachModal(true);
  };

  const handleViewHistory = (policy: Policy) => {
    setSelectedPolicy(policy);
    setShowHistoryModal(true);
  };

  const handleClonePolicy = (policy: Policy) => {
    setSelectedPolicy(policy);
    setShowCloneModal(true);
  };

  const handleDeletePolicy = async (policy: Policy) => {
    const confirmMessage = t(
      'admin.policyManagement.confirmDelete',
      `Are you sure you want to delete the policy "${policy.name}"?`
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await apiClient.deletePolicy(policy.id);

      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(t('admin.policyManagement.policyDeleted', 'Policy deleted successfully'));
      loadPolicies();
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Failed to delete policy';
      toast.error(errorMessage);
    }
  };

  const handleSearch = () => {
    // For now, client-side filtering
    // In production, you might want to add backend search support
    loadPolicies();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const filteredPolicies = searchTerm
    ? policies.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : policies;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="policy-management">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>{t('admin.policyManagement.title', 'Policy Management')}</h3>
        <div>
          <Button variant="primary" onClick={handleCreatePolicy}>
            <i className="bi bi-plus-circle me-2"></i>
            {t('admin.policyManagement.createPolicy', 'Create Policy')}
          </Button>
          <Button variant="outline-secondary" className="ms-2" onClick={loadPolicies}>
            <i className="bi bi-arrow-clockwise"></i>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={t('admin.policyManagement.searchPlaceholder', 'Search policies by name or description...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button variant="outline-secondary" onClick={handleSearch}>
                  {t('admin.policyManagement.search', 'Search')}
                </Button>
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterTemplate === undefined ? 'all' : filterTemplate ? 'templates' : 'policies'}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterTemplate(
                    value === 'all' ? undefined : value === 'templates'
                  );
                  setCurrentPage(1);
                }}
              >
                <option value="all">{t('admin.policyManagement.allPolicies', 'All Policies')}</option>
                <option value="policies">{t('admin.policyManagement.policiesOnly', 'Policies Only')}</option>
                <option value="templates">{t('admin.policyManagement.templatesOnly', 'Templates Only')}</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={perPage}
                onChange={(e) => {
                  setPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value="10">10 {t('admin.policyManagement.perPage', 'per page')}</option>
                <option value="25">25 {t('admin.policyManagement.perPage', 'per page')}</option>
                <option value="50">50 {t('admin.policyManagement.perPage', 'per page')}</option>
                <option value="100">100 {t('admin.policyManagement.perPage', 'per page')}</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Policies Table */}
      {isLoading ? (
        <Alert variant="info">{t('admin.policyManagement.loading', 'Loading policies...')}</Alert>
      ) : filteredPolicies.length === 0 ? (
        <Alert variant="info">
          {searchTerm
            ? t('admin.policyManagement.noSearchResults', 'No policies match your search')
            : t('admin.policyManagement.noPolicies', 'No policies found. Create your first policy to get started.')}
        </Alert>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('admin.policyManagement.name', 'Name')}</th>
                <th>{t('admin.policyManagement.description', 'Description')}</th>
                <th>{t('admin.policyManagement.type', 'Type')}</th>
                <th>{t('admin.policyManagement.statements', 'Statements')}</th>
                <th>{t('admin.policyManagement.created', 'Created')}</th>
                <th>{t('admin.policyManagement.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPolicies.map((policy, idx) => (
                <tr key={policy.id}>
                  <td>{(currentPage - 1) * perPage + idx + 1}</td>
                  <td>
                    <strong>{policy.name}</strong>
                  </td>
                  <td>
                    <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {policy.description || <span className="text-muted fst-italic">No description</span>}
                    </div>
                  </td>
                  <td>
                    {policy.is_template ? (
                      <Badge bg="info">Template</Badge>
                    ) : (
                      <Badge bg="secondary">Policy</Badge>
                    )}
                  </td>
                  <td>{policy.policy_document.Statement.length}</td>
                  <td>{formatDate(policy.created_at)}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-primary" size="sm">
                        {t('admin.policyManagement.actions', 'Actions')}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleEditPolicy(policy)}>
                          {t('admin.policyManagement.edit', 'Edit')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleAttachPolicy(policy)}>
                          {t('admin.policyManagement.attachToDatasets', 'Attach to Datasets')}
                        </Dropdown.Item>
                        {policy.is_template && (
                          <Dropdown.Item onClick={() => handleClonePolicy(policy)}>
                            {t('admin.policyManagement.clone', 'Clone')}
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={() => handleViewHistory(policy)}>
                          {t('admin.policyManagement.viewHistory', 'View History')}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => handleDeletePolicy(policy)}
                          className="text-danger"
                        >
                          {t('admin.policyManagement.delete', 'Delete')}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                {t(
                  'admin.policyManagement.showing',
                  `Showing ${(currentPage - 1) * perPage + 1} to ${Math.min(currentPage * perPage, totalPolicies)} of ${totalPolicies} policies`
                )}
              </div>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="me-2"
                >
                  {t('admin.policyManagement.previous', 'Previous')}
                </Button>
                <span className="mx-2">
                  {t('admin.policyManagement.page', 'Page')} {currentPage} {t('admin.policyManagement.of', 'of')} {totalPages}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="ms-2"
                >
                  {t('admin.policyManagement.next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <PolicyEditorModal
        show={showEditorModal}
        onHide={() => setShowEditorModal(false)}
        onSuccess={loadPolicies}
        policy={selectedPolicy}
        studies={studies}
        clinics={clinics}
        devices={devices}
        dataTypes={dataTypes}
        users={users}
        userGroups={userGroups}
        roles={roles}
        onRefreshStudies={handleRefreshStudies}
        onRefreshClinics={handleRefreshClinics}
        onRefreshDevices={handleRefreshDevices}
        onRefreshDataTypes={handleRefreshDataTypes}
        onRefreshUsers={handleRefreshUsers}
        onRefreshUserGroups={handleRefreshUserGroups}
        onRefreshRoles={handleRefreshRoles}
        isRefreshingStudies={isRefreshingStudies}
        isRefreshingClinics={isRefreshingClinics}
        isRefreshingDevices={isRefreshingDevices}
        isRefreshingDataTypes={isRefreshingDataTypes}
        isRefreshingUsers={isRefreshingUsers}
        isRefreshingUserGroups={isRefreshingUserGroups}
        isRefreshingRoles={isRefreshingRoles}
      />

      <PolicyAttachModal
        show={showAttachModal}
        onHide={() => setShowAttachModal(false)}
        onSuccess={loadPolicies}
        policy={selectedPolicy}
      />

      <PolicyHistoryModal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        policy={selectedPolicy}
      />

      <PolicyCloneModal
        show={showCloneModal}
        onHide={() => setShowCloneModal(false)}
        onSuccess={(clonedPolicy) => {
          loadPolicies();
          if (clonedPolicy) {
            // Optionally open editor for the cloned policy
            setSelectedPolicy(clonedPolicy);
            setShowEditorModal(true);
          }
        }}
        policy={selectedPolicy}
      />
    </div>
  );
}
